import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

// Miroir des réponses de boostcore `admin/factor/exports` (FactorExportController).

export type FactorInvoiceRowStatus = 'NON_EXPORTE' | 'EXPORTED' | 'ACCEPTED' | 'REFUSED'

export interface FactorInvoiceRow {
  invoiceUuid: string
  invoiceName: string | null
  companyName: string | null
  invoiceDate: string | null // YYYY-MM-DD
  invoiceDateDue: string | null
  amount: number | null
  currencyCode: string | null
  paymentState: string | null
  factorStatus: FactorInvoiceRowStatus
  exportId: string | null
  exportedAt: string | null
  decisionAt: string | null
  refusalReason: string | null
  fundedByFactorAt: string | null
  fundedAmount: number | null
  factorSettledAt: string | null
  notes: string | null
}

export interface FactorExportRow {
  id: string
  status: 'READY' | 'FAILED'
  dateFrom: string | null
  dateTo: string | null
  trigger: 'MANUAL' | 'CRON' | null
  triggeredBy: string | null
  lineCount: number
  totalAmount: number | null
  createdAt: string
  generatedAt: string | null
  filename: string | null
  hasFile: boolean
  archiveFilename: string | null
  hasArchive: boolean
}

export interface FactorExportDetail extends FactorExportRow {
  lines: FactorInvoiceRow[]
}

export interface GenerateFactorExportResult {
  exportId: string | null
  period: { from: string; to: string }
  paymentModeLabels: string[]
  count: number
  totalAmount: number
  filename: string | null
  archiveFilename: string | null
  archiveError: string | null
  missingPdfs: string[]
  excluded: Array<{ invoiceName: string; reason: string }>
}

export interface FactorLifecyclePayload {
  status?: 'EXPORTED' | 'ACCEPTED' | 'REFUSED'
  decisionAt?: string | null
  refusalReason?: string | null
  fundedByFactorAt?: string | null
  fundedAmount?: number | null
  factorSettledAt?: string | null
  notes?: string | null
}

export interface FactorInvoiceFilters {
  dateFrom?: string
  dateTo?: string
  status?: FactorInvoiceRowStatus | ''
  q?: string
  page?: number
  pageSize?: number
}

export const useFactorDepositStore = defineStore('factorDeposit', () => {
  const suiteStore = useSuiteStore()

  const invoices = ref<FactorInvoiceRow[]>([])
  const invoicesTotal = ref(0)
  const isLoadingInvoices = ref(false)

  const exports = ref<FactorExportRow[]>([])
  const isLoadingExports = ref(false)
  const isGenerating = ref(false)

  async function fetchInvoices(filters: FactorInvoiceFilters = {}): Promise<void> {
    isLoadingInvoices.value = true
    try {
      const qs = new URLSearchParams()
      if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom)
      if (filters.dateTo) qs.set('dateTo', filters.dateTo)
      if (filters.status) qs.set('status', filters.status)
      if (filters.q) qs.set('q', filters.q)
      if (filters.page) qs.set('page', String(filters.page))
      if (filters.pageSize) qs.set('pageSize', String(filters.pageSize))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      const res = await suiteStore.suiteFetch(`admin/factor/exports/invoices${suffix}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const body = (await res.json()) as { data: FactorInvoiceRow[]; total: number }
        invoices.value = body.data ?? []
        invoicesTotal.value = body.total ?? 0
      }
    } catch (e) {
      console.error('Erreur récupération factures BNP Factor', e)
    } finally {
      isLoadingInvoices.value = false
    }
  }

  async function fetchExports(limit = 50): Promise<void> {
    isLoadingExports.value = true
    try {
      const res = await suiteStore.suiteFetch(`admin/factor/exports?limit=${limit}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        exports.value = ((await res.json()) as FactorExportRow[]) ?? []
      }
    } catch (e) {
      console.error('Erreur récupération dépôts BNP Factor', e)
    } finally {
      isLoadingExports.value = false
    }
  }

  async function fetchExportDetail(id: string): Promise<FactorExportDetail | null> {
    const res = await suiteStore.suiteFetch(`admin/factor/exports/${id}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as FactorExportDetail
    }
    return null
  }

  async function generate(
    dateFrom: string,
    dateTo: string,
    forceReexport = false,
  ): Promise<{ ok: boolean; data?: GenerateFactorExportResult; message?: string }> {
    isGenerating.value = true
    try {
      const res = await suiteStore.suiteFetch('admin/factor/exports/generate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dateFrom, dateTo, forceReexport }),
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        return { ok: true, data: (await res.json()) as GenerateFactorExportResult }
      }
      let message = 'Échec de la génération'
      if (res && typeof res !== 'boolean') {
        try {
          const b = await res.json()
          if (b?.message) message = Array.isArray(b.message) ? b.message.join(', ') : String(b.message)
        } catch {
          // ignore
        }
      }
      return { ok: false, message }
    } catch (e) {
      return { ok: false, message: (e as Error).message }
    } finally {
      isGenerating.value = false
    }
  }

  async function updateLifecycle(
    invoiceUuid: string,
    payload: FactorLifecyclePayload,
  ): Promise<FactorInvoiceRow | null> {
    const res = await suiteStore.suiteFetch(`admin/factor/exports/invoices/${invoiceUuid}/lifecycle`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const updated = (await res.json()) as FactorInvoiceRow
      const idx = invoices.value.findIndex((i) => i.invoiceUuid === invoiceUuid)
      if (idx !== -1) invoices.value[idx] = { ...invoices.value[idx], ...updated }
      return updated
    }
    return null
  }

  async function downloadByPath(urlPath: string, fallbackName: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(urlPath, { method: 'GET', credentials: 'include' })
    if (!res || typeof res === 'boolean' || !res.ok) return false
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fallbackName
    a.click()
    URL.revokeObjectURL(url)
    return true
  }

  function downloadFile(exportId: string, filename?: string | null): Promise<boolean> {
    return downloadByPath(`admin/factor/exports/${exportId}/download`, filename || `${exportId}.xlsx`)
  }

  function downloadArchive(exportId: string, filename?: string | null): Promise<boolean> {
    return downloadByPath(`admin/factor/exports/${exportId}/archive`, filename || `${exportId}.zip`)
  }

  return {
    invoices,
    invoicesTotal,
    isLoadingInvoices,
    exports,
    isLoadingExports,
    isGenerating,
    fetchInvoices,
    fetchExports,
    fetchExportDetail,
    generate,
    updateLifecycle,
    downloadFile,
    downloadArchive,
  }
})
